import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import PageLayout from "./layouts/PageLayout";
import Section from "./layouts/Section";
import servicesData from "../data/servicesData";

const ServiceDetail = () => {
  const { slug } = useParams();
  const service = servicesData[slug];
  const [openFaq, setOpenFaq] = useState(null);

  if (!service) {
    return (
      <PageLayout title="Service Not Found | Cell Station">
        <Section title="Service Not Found">
          <div className="section">
            <div className="container text-center" style={{ padding: "60px 0" }}>
              <h2 className="mb-20">Service Not Found</h2>
              <p className="text-muted mb-25">
                Sorry, we couldn't find that service page.
              </p>
              <Link to="/services" className="btn btn-primary">
                View All Services
              </Link>
            </div>
          </div>
        </Section>
      </PageLayout>
    );
  }

  const canonical = `https://www.icellstation.com/services/${service.slug}`;

  const serviceJsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: service.title,
    description: service.metaDescription,
    provider: {
      "@type": "LocalBusiness",
      name: "Cell Station",
      telephone: "+1-215-939-3933",
      address: {
        "@type": "PostalAddress",
        streetAddress: "2500 W Moreland Rd, Space #1077",
        addressLocality: "Willow Grove",
        addressRegion: "PA",
        postalCode: "19090",
        addressCountry: "US",
      },
    },
    areaServed: {
      "@type": "City",
      name: "Willow Grove",
    },
  };

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: service.faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };

  return (
    <PageLayout
      title={service.metaTitle}
      description={service.metaDescription}
      url={canonical}
    >
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(serviceJsonLd)}
        </script>
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      </Helmet>

      <Section title={service.title}>
        <div className="section">
          <div className="container">
            {/* Breadcrumb */}
            <div className="mb-30">
              <Link to="/services" style={{ color: "#F50963" }}>
                <i className="fas fa-arrow-left mr-2" />
                All Services
              </Link>
            </div>

            <div className="row">
              {/* Main Content */}
              <div className="col-lg-8">
                {/* Hero */}
                <div
                  style={{
                    background: "linear-gradient(135deg, #18305d 0%, #0f1d3a 100%)",
                    borderRadius: "16px",
                    padding: "40px",
                    marginBottom: "30px",
                    color: "#ffffff",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", marginBottom: "15px" }}>
                    <i
                      className={service.icon}
                      style={{ fontSize: "2.5rem", color: "#ffaa19", marginRight: "15px" }}
                    />
                    <h1 style={{ margin: 0, fontSize: "2rem" }}>{service.title}</h1>
                  </div>
                  <p style={{ fontSize: "1.1rem", opacity: 0.9, margin: 0 }}>
                    {service.heroText}
                  </p>
                </div>

                {/* Description */}
                <div style={{ marginBottom: "30px" }}>
                  <h2 className="h4 text-dark mb-15">About This Service</h2>
                  <p className="text-muted" style={{ lineHeight: "1.8" }}>
                    {service.description}
                  </p>
                </div>

                {/* What's Included */}
                <div
                  style={{
                    background: "#f8f9fa",
                    borderRadius: "12px",
                    padding: "30px",
                    marginBottom: "30px",
                  }}
                >
                  <h2 className="h4 text-dark mb-20">What's Included</h2>
                  <ul
                    className="check-list"
                    style={{ columns: "2", columnGap: "30px" }}
                  >
                    {service.features.map((feature, index) => (
                      <li key={index} style={{ marginBottom: "10px", breakInside: "avoid" }}>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* FAQs */}
                <div style={{ marginBottom: "30px" }}>
                  <h2 className="h4 text-dark mb-20">Frequently Asked Questions</h2>
                  {service.faqs.map((faq, index) => (
                    <div
                      key={index}
                      style={{
                        border: "1px solid #e9ecef",
                        borderRadius: "8px",
                        marginBottom: "10px",
                        overflow: "hidden",
                      }}
                    >
                      <button
                        onClick={() => setOpenFaq(openFaq === index ? null : index)}
                        style={{
                          width: "100%",
                          textAlign: "left",
                          padding: "18px 20px",
                          background: openFaq === index ? "#f8f9fa" : "#ffffff",
                          border: "none",
                          cursor: "pointer",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          fontWeight: "600",
                          fontSize: "1rem",
                          color: "#333",
                        }}
                      >
                        {faq.question}
                        <i
                          className={`fas fa-chevron-${openFaq === index ? "up" : "down"}`}
                          style={{ color: "#F50963", fontSize: "0.85rem" }}
                        />
                      </button>
                      {openFaq === index && (
                        <div
                          style={{
                            padding: "15px 20px 20px",
                            color: "#666",
                            lineHeight: "1.7",
                            borderTop: "1px solid #e9ecef",
                          }}
                        >
                          {faq.answer}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Sidebar */}
              <div className="col-lg-4">
                {/* Pricing Card */}
                <div
                  style={{
                    background: "#ffffff",
                    borderRadius: "12px",
                    padding: "30px",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                    marginBottom: "25px",
                    position: "sticky",
                    top: "20px",
                  }}
                >
                  <h3 className="h5 text-dark mb-15">
                    <i className="fas fa-tag text-primary mr-2" />
                    Pricing
                  </h3>
                  <p
                    style={{
                      fontSize: "1.5rem",
                      fontWeight: "700",
                      color: "#18305d",
                      marginBottom: "5px",
                    }}
                  >
                    {service.pricing}
                  </p>
                  <p className="text-muted" style={{ fontSize: "0.85rem", marginBottom: "20px" }}>
                    Free diagnostic included
                  </p>

                  <Link
                    to="/contact-us"
                    className="btn btn-primary w-100 mb-15"
                    style={{ padding: "14px 20px" }}
                  >
                    <i className="fas fa-comment-dots mr-2" />
                    Get a Free Quote
                  </Link>

                  <a
                    href="tel:+12159393933"
                    className="btn w-100"
                    style={{
                      padding: "14px 20px",
                      background: "#18305d",
                      color: "#ffffff",
                      borderRadius: "5px",
                    }}
                  >
                    <i className="fas fa-phone-alt mr-2" />
                    Call (215) 939-3933
                  </a>

                  {/* Trust badges */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      gap: "15px",
                      marginTop: "20px",
                      flexWrap: "wrap",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "0.75rem",
                        color: "#666",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <i className="fas fa-shield-alt text-primary mr-1" />
                      30-Day Warranty
                    </span>
                    <span
                      style={{
                        fontSize: "0.75rem",
                        color: "#666",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <i className="fas fa-clock text-primary mr-1" />
                      Same-Day Service
                    </span>
                  </div>
                </div>

                {/* Other Services */}
                <div
                  style={{
                    background: "#f8f9fa",
                    borderRadius: "12px",
                    padding: "25px",
                  }}
                >
                  <h4 className="h5 text-dark mb-15">Other Services</h4>
                  <ul className="list-unstyled">
                    {Object.values(servicesData)
                      .filter((s) => s.slug !== service.slug)
                      .map((s) => (
                        <li key={s.slug} style={{ marginBottom: "8px" }}>
                          <Link
                            to={`/services/${s.slug}`}
                            style={{ color: "#4a4a4a", display: "flex", alignItems: "center" }}
                          >
                            <i
                              className={s.icon}
                              style={{ width: "20px", color: "#F50963", fontSize: "0.85rem" }}
                            />
                            <span style={{ marginLeft: "8px" }}>{s.title}</span>
                          </Link>
                        </li>
                      ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section>
    </PageLayout>
  );
};

export default ServiceDetail;
